import { ResponsiveContainer, BarChart, CartesianGrid, XAxis, YAxis, Tooltip, Bar } from "recharts";
import { Paper, Typography, useTheme } from "@mui/material";
import { ViewByClaimType } from "@/types";
import { getFormattedValue, useChartPalette } from "../utils/chartUtils";
import { EmptyChartState } from "@/components/common/EmptyChartState";
import { formatUSD } from "@/utils/formatters/currencyFormatter";

type ClaimsByAverageDaysToProcessData = {
  name: string;
  claims: number;
  amount: number;
};

type Props = {
  data: ClaimsByAverageDaysToProcessData[];
  viewBy: ViewByClaimType;
  isFullscreen: boolean;
};

const BucketTooltip = ({
  active,
  payload,
  totalClaims,
  hasAmount,
}: {
  active?: boolean;
  payload?: { payload: ClaimsByAverageDaysToProcessData }[];
  totalClaims: number;
  hasAmount: boolean;
}) => {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  const pct = totalClaims ? ((p.claims / totalClaims) * 100).toFixed(0) : "0";
  return (
    <Paper elevation={3} sx={{ p: 1.5 }}>
      <Typography variant="subtitle2">{p.name}</Typography>
      <Typography variant="body2">
        <strong>{p.claims.toLocaleString()}</strong> claims ({pct}% of total)
      </Typography>
      {hasAmount && <Typography variant="body2">{formatUSD(p.amount)} paid</Typography>}
    </Paper>
  );
};

const ClaimsByAverageDaysToProcessChart = ({ data, viewBy, isFullscreen }: Props) => {
  const chartPalette = useChartPalette();
  const theme = useTheme();
  const isAmountViewByClaimType = viewBy === ViewByClaimType.CLAIM_AMOUNT;
  const dataKey = isAmountViewByClaimType ? "amount" : "claims";

  if (!data.length) return <EmptyChartState />;

  const totalClaims = data.reduce((sum, d) => sum + d.claims, 0);
  const hasAmount = data.some((d) => d.amount > 0);

  return (
    <ResponsiveContainer width="100%" height={isFullscreen ? 500 : "100%"}>
      <BarChart data={data} margin={{ top: 10, right: 20, left: 10, bottom: 0 }}>
        <defs>
          <linearGradient id="avgDaysFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={chartPalette[3]} stopOpacity={0.95} />
            <stop offset="100%" stopColor={chartPalette[3]} stopOpacity={0.5} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={theme.palette.divider} />
        <XAxis
          dataKey="name"
          axisLine={false}
          tickLine={false}
          tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
        />
        <YAxis
          axisLine={false}
          tickLine={false}
          tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
          tickFormatter={(value: number) => getFormattedValue(value, viewBy)}
        />
        <Tooltip
          cursor={{ fill: theme.palette.action.hover }}
          content={(props) => (
            <BucketTooltip
              active={props.active}
              payload={props.payload as unknown as { payload: ClaimsByAverageDaysToProcessData }[] | undefined}
              totalClaims={totalClaims}
              hasAmount={hasAmount}
            />
          )}
        />
        <Bar
          dataKey={dataKey}
          fill="url(#avgDaysFill)"
          radius={[6, 6, 0, 0]}
          maxBarSize={64}
          activeBar={{ fillOpacity: 1, stroke: theme.palette.background.paper, strokeWidth: 2 }}
        />
      </BarChart>
    </ResponsiveContainer>
  );
};

export default ClaimsByAverageDaysToProcessChart;
